import React, { useState, useRef } from 'react';
import { Container, Modal, Button, Row, Col, Card, Badge, Alert, Spinner } from 'react-bootstrap';
import { Eye } from 'lucide-react';
import PortfolioProjectsList from './PortfolioProjectsList';
import PortfolioProjectForm from './PortfolioProjectForm';
import { portfolioProjectService } from '../services/portfolioProjectService';
import './Email.css';

const PortfolioProjects = () => {
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showProjectView, setShowProjectView] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewingProject, setViewingProject] = useState(null);
  const [loadingView, setLoadingView] = useState(false);
  const projectsListRef = useRef(null);

  const handleViewProject = async (projectId) => {
    setSelectedProjectId(projectId);
    setLoadingView(true);
    try {
      const response = await portfolioProjectService.getPortfolioProjectById(projectId);
      if (response.success && response.data) {
        setViewingProject(response.data);
        setShowProjectView(true);
      }
    } catch (error) {
      alert(`Failed to load project: ${error.message}`);
    } finally {
      setLoadingView(false);
    }
  };

  const handleEditProject = (projectId) => {
    setEditingProjectId(projectId);
    setShowProjectForm(true);
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await portfolioProjectService.deletePortfolioProject(projectId);
        // Force refresh by updating key
        setRefreshKey(prev => prev + 1);
      } catch (error) {
        alert(`Failed to delete project: ${error.message}`);
      }
    }
  };

  const handleCreateProject = () => {
    setEditingProjectId(null);
    setShowProjectForm(true);
  };

  const handleFormSuccess = () => {
    // Force refresh by updating key
    setRefreshKey(prev => prev + 1);
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'success',
      'in-progress': 'warning',
      upcoming: 'info'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status?.replace('-', ' ').toUpperCase() || 'N/A'}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const variants = {
      Commercial: 'primary',
      Industrial: 'info',
      Residential: 'success'
    };
    return <Badge bg={variants[category] || 'secondary'}>{category}</Badge>;
  };

  return (
    <Container fluid className="email-page">
      {/* Header */}
      <div className="email-header">
        <div className="mb-4">
          <h2 className="email-page-title">Portfolio Projects</h2>
          <p className="text-muted mb-0">Manage your portfolio projects showcase</p>
        </div>
      </div>

      {/* Projects List */}
      <PortfolioProjectsList
        key={refreshKey}
        ref={projectsListRef}
        onViewProject={handleViewProject}
        onEditProject={handleEditProject}
        onDeleteProject={handleDeleteProject}
        onCreateProject={handleCreateProject}
      />

      {/* Project View Modal */}
      <Modal show={showProjectView} onHide={() => {
        setShowProjectView(false);
        setSelectedProjectId(null);
        setViewingProject(null);
      }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {viewingProject?.name}
            {viewingProject && (
              <>
                {getCategoryBadge(viewingProject.category)}
                <span className="ms-2">{getStatusBadge(viewingProject.status)}</span>
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingView ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : viewingProject ? (
            <div>
              {viewingProject.image && (
                <div className="mb-3 text-center">
                  <img
                    src={viewingProject.image}
                    alt={viewingProject.imageAlt || viewingProject.name}
                    style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px' }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                    }}
                  />
                </div>
              )}

              <Card className="mb-3">
                <Card.Header>
                  <h5 className="mb-0">Project Details</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Name:</strong> {viewingProject.name}</p>
                      <p><strong>Category:</strong> {getCategoryBadge(viewingProject.category)}</p>
                      <p><strong>Location:</strong> {viewingProject.location}</p>
                      <p><strong>Year:</strong> {viewingProject.year}</p>
                      <p><strong>Status:</strong> {getStatusBadge(viewingProject.status)}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Featured:</strong> {viewingProject.featured ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</p>
                      <p><strong>Display Order:</strong> {viewingProject.displayOrder || 0}</p>
                      {viewingProject.clientName && (
                        <p><strong>Client:</strong> {viewingProject.clientName}</p>
                      )}
                      {viewingProject.projectSize && (
                        <p><strong>Project Size:</strong> {viewingProject.projectSize}</p>
                      )}
                      {viewingProject.startDate && (
                        <p><strong>Start Date:</strong> {new Date(viewingProject.startDate).toLocaleDateString()}</p>
                      )}
                      {viewingProject.completionDate && (
                        <p><strong>Completion Date:</strong> {new Date(viewingProject.completionDate).toLocaleDateString()}</p>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Header>
                  <h5 className="mb-0">Description</h5>
                </Card.Header>
                <Card.Body>
                  <p>{viewingProject.description}</p>
                </Card.Body>
              </Card>

              {viewingProject.tags && Array.isArray(viewingProject.tags) && viewingProject.tags.length > 0 && (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Tags</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="d-flex flex-wrap gap-2">
                      {viewingProject.tags.map((tag, index) => (
                        <Badge key={index} bg="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </div>
          ) : (
            <Alert variant="danger">Failed to load project details</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowProjectView(false);
            setSelectedProjectId(null);
            setViewingProject(null);
          }}>
            Close
          </Button>
          <Button variant="primary" onClick={() => {
            setShowProjectView(false);
            handleEditProject(selectedProjectId);
          }}>
            Edit Project
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Project Form Modal */}
      <PortfolioProjectForm
        show={showProjectForm}
        onHide={() => {
          setShowProjectForm(false);
          setEditingProjectId(null);
        }}
        projectId={editingProjectId}
        onSuccess={handleFormSuccess}
      />
    </Container>
  );
};

export default PortfolioProjects;

